import styled, { keyframes } from 'styled-components'
import { Modal } from 'react-bootstrap'
import {
  createMeetingRoomModalDialogStyles,
  meetingRoomModalHeaderStyles,
} from 'domain/feature/hub/company/meeting-room/shared/meetingRoomModalStyles'
import { SLOT_HEIGHT_PX } from './dateUtils'

const AVAILABILITY_MODAL_WIDTH = '1200px'
const AVAILABILITY_MODAL_HEIGHT = 'min(900px, calc(100vh - 48px))'

export const StyledModal = styled(Modal)`
  ${createMeetingRoomModalDialogStyles(AVAILABILITY_MODAL_WIDTH)}
  ${meetingRoomModalHeaderStyles}

  &.modal {
    display: flex !important;
    align-items: center;
    justify-content: center;
  }

  .modal-dialog {
    margin: auto;
    height: ${AVAILABILITY_MODAL_HEIGHT};
    max-height: ${AVAILABILITY_MODAL_HEIGHT};
  }

  .modal-content {
    height: 100%;
    max-height: ${AVAILABILITY_MODAL_HEIGHT};
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .modal-body {
    padding: 0;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: var(--white);
    position: relative;
  }

  @media (max-width: 839px) {
    .modal-dialog {
      height: calc(100vh - 24px);
      max-height: calc(100vh - 24px);
    }

    .modal-content {
      max-height: calc(100vh - 24px);
    }
  }
`

export const CalendarShell = styled.div<{ $variant?: 'modal' | 'page' }>`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  height: 100%;
  position: relative;
  background: var(--white);
  overflow: hidden;

  ${({ $variant }) =>
    $variant === 'page' &&
    `
    min-height: min(820px, calc(100vh - 180px));
    height: min(820px, calc(100vh - 180px));
    border-radius: 12px;
  `}
`

export const CalendarBody = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  height: 100%;
`

export const Sidebar = styled.aside`
  width: 260px;
  flex-shrink: 0;
  padding: 20px 16px;
  background: var(--white);
  overflow-y: auto;

  @media (max-width: 900px) {
    display: none;
  }
`

export const SidebarTitle = styled.h3`
  margin: 0 0 14px;
  padding: 0 10px;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  letter-spacing: 0.8px;
`

export const SpaceItem = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  font-size: 13px;
  border-radius: 8px;
  cursor: pointer;
  color: var(--text-primary);
  transition: background 0.15s;
  min-width: 0;

  &:hover {
    background: var(--primary-hover-bg);
  }
`

export const SpaceCheckbox = styled.input.attrs({ type: 'checkbox' })<{
  $color: string
}>`
  appearance: none;
  -webkit-appearance: none;
  width: 20px;
  height: 20px;
  margin: 0;
  flex-shrink: 0;
  border-radius: 4px;
  border: 2px solid ${({ $color }) => $color};
  background: transparent;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  &:checked {
    background: ${({ $color }) => $color};
    border-color: ${({ $color }) => $color};
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='none' stroke='%23fff' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round' d='M3.5 8.5l3 3 6-6'/%3E%3C/svg%3E");
    background-size: 14px 14px;
    background-position: center;
    background-repeat: no-repeat;
  }

  &:focus-visible {
    outline: 2px solid ${({ $color }) => $color};
    outline-offset: 2px;
  }
`

export const SpaceName = styled.span`
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const CalendarGrid = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin: 0 12px 12px 0;
  position: relative;
`

export const CalendarStatusOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(2px);
  padding: 24px;
`

export const CalendarLoadingContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
`

export const CalendarStatusMessage = styled.p`
  margin: 0;
  text-align: center;
  font-size: 14px;
  color: var(--text-secondary);
`

export const RetryAvailabilityButton = styled.button`
  border: 1px solid var(--primary);
  background: var(--white);
  color: var(--primary);
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background: var(--primary-hover-bg);
  }
`

export const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 20px;
  background: var(--white);
  flex-shrink: 0;
  flex-wrap: wrap;
`

export const ToolbarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
  flex: 1;
`

export const NavGroup = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`

export const NavButton = styled.button`
  width: 32px;
  height: 32px;
  border: 1px solid var(--primary);
  background-color: var(--white);
  cursor: pointer;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary);
  transition: background 0.2s;
  line-height: 1;
  padding: 0;

  &:hover {
    background-color: var(--primary-hover-bg);
  }

  &:first-child {
    border-radius: 4px 0 0 4px;
  }

  &:last-child {
    border-radius: 0 4px 4px 0;
  }
`

export const TodayButton = styled.button`
  padding: 6px 16px;
  border: 1px solid var(--primary);
  border-left: none;
  border-right: none;
  background: var(--primary);
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: var(--white);
  transition: background 0.2s;
  height: 32px;

  &:hover {
    filter: brightness(0.92);
  }
`

export const ToolbarCenter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  min-width: 0;
`

export const CurrentDateLabel = styled.span`
  font-size: 15px;
  font-weight: 500;
  color: var(--text-primary);
  text-transform: capitalize;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const ViewButtons = styled.div`
  display: flex;
  border-radius: 4px;
  overflow: hidden;
`

export const ViewButton = styled.button<{ $active?: boolean }>`
  padding: 7px 18px;
  border: 1px solid var(--primary);
  background: ${({ $active }) =>
    $active ? 'var(--primary-muted)' : 'var(--primary)'};
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: var(--white);
  transition: all 0.2s;
  margin-left: -1px;

  &:first-child {
    border-radius: 4px 0 0 4px;
    margin-left: 0;
  }

  &:last-child {
    border-radius: 0 4px 4px 0;
  }

  &:hover:not(:disabled) {
    background: ${({ $active }) =>
      $active ? 'var(--primary-muted)' : 'var(--primary-dark)'};
  }
`

export const ViewPanel = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--border-light);
  border-radius: 8px;
  background: var(--white);
`

export const WeekHeader = styled.div<{ $dayCount: number }>`
  display: grid;
  grid-template-columns: 64px repeat(${({ $dayCount }) => $dayCount}, 1fr);
  border-bottom: 1px solid var(--border-light);
  background: var(--white);
  flex-shrink: 0;
`

export const WeekHeaderCell = styled.div`
  padding: 8px 0 12px;
  text-align: center;
  border-left: 1px solid var(--border-light);

  &:first-child {
    border-left: none;
  }
`

export const DayName = styled.div`
  font-size: 11px;
  font-weight: 500;
  color: var(--grey-lighter);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

export const DayNumberButton = styled.button<{
  $today?: boolean
  $size?: 'week' | 'month'
  $muted?: boolean
}>`
  font-size: ${({ $size }) => ($size === 'month' ? '12px' : '26px')};
  font-weight: ${({ $today, $size }) => ($today && $size === 'month' ? 500 : 400)};
  margin-top: ${({ $size }) => ($size === 'month' ? '0' : '2px')};
  color: ${({ $today, $muted }) =>
    $today ? 'var(--white)' : $muted ? 'var(--border)' : 'var(--text-primary)'};
  width: ${({ $size }) => ($size === 'month' ? '24px' : '44px')};
  height: ${({ $size }) => ($size === 'month' ? '24px' : '44px')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: none;
  background: ${({ $today }) => ($today ? 'var(--primary)' : 'transparent')};
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: ${({ $today }) => ($today ? 'var(--primary)' : 'var(--primary-hover-bg)')};
    color: ${({ $today }) => ($today ? 'var(--white)' : 'var(--primary)')};
    filter: ${({ $today }) => ($today ? 'brightness(0.92)' : 'none')};
  }
`

export const TimeGridWrapper = styled.div`
  overflow-y: auto;
  flex: 1;
  position: relative;
  min-height: 0;
`

export const WeekTimeGrid = styled.div<{ $dayCount: number }>`
  display: grid;
  grid-template-columns: 64px repeat(${({ $dayCount }) => $dayCount}, 1fr);
  position: relative;
`

export const DayTimeGrid = styled.div`
  display: grid;
  grid-template-columns: 64px 1fr;
  position: relative;
`

export const TimeLabel = styled.div<{ $isFirst?: boolean }>`
  height: ${SLOT_HEIGHT_PX}px;
  padding-right: 8px;
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  font-size: 11px;
  color: var(--grey-lighter);
  border-right: 1px solid var(--border-light);

  span {
    transform: ${({ $isFirst }) => ($isFirst ? 'translateY(2px)' : 'translateY(-8px)')};
  }
`

export const DayColumn = styled.div`
  border-left: 1px solid var(--border-light);
  position: relative;
  height: ${SLOT_HEIGHT_PX}px;
  border-bottom: 1px solid var(--border-light);
  overflow: visible;
`

export const EventBlock = styled.div<{
  $color: string
  $top: number
  $height: number
  $zIndex: number
  $compact?: boolean
}>`
  position: absolute;
  left: 2px;
  right: 2px;
  top: ${({ $top }) => $top}px;
  height: ${({ $height }) => $height}px;
  border-radius: 6px;
  padding: ${({ $compact }) => ($compact === true ? '2px 6px' : '4px 8px')};
  font-size: 11px;
  color: var(--white);
  overflow: hidden;
  cursor: pointer;
  z-index: ${({ $zIndex }) => $zIndex};
  background: ${({ $color }) => $color};
  box-shadow: 0 1px 2px var(--shadow-sm);
  transition: transform 0.15s, box-shadow 0.15s;
  display: flex;
  flex-direction: column;
  justify-content: ${({ $compact }) => ($compact === true ? 'center' : 'flex-start')};

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px var(--shadow-md);
    z-index: 1000;
  }
`

export const EventTitle = styled.div`
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 10px;
  line-height: 1.2;
`

export const EventTime = styled.div`
  font-size: 10px;
  opacity: 0.9;
  margin-top: 1px;
`

export const EventSpaceLabel = styled.div`
  font-size: 11px;
  opacity: 0.95;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const TimeIndicator = styled.div<{ $top: number; $left?: string }>`
  position: absolute;
  left: ${({ $left }) => $left ?? '64px'};
  right: 0;
  top: ${({ $top }) => $top}px;
  height: 2px;
  background: var(--red);
  z-index: 9;
  pointer-events: none;

  &::before {
    content: '';
    position: absolute;
    left: -5px;
    top: -4px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: var(--red);
  }
`

export const DayViewHeader = styled.div`
  padding: 12px 24px;
  border-bottom: 1px solid var(--border-light);
  display: flex;
  align-items: baseline;
  gap: 12px;
  flex-shrink: 0;
`

export const DayViewNumber = styled.div<{ $today?: boolean }>`
  font-size: 42px;
  font-weight: 400;
  color: ${({ $today }) => ($today ? 'var(--primary)' : 'var(--text-primary)')};
  line-height: 1;
`

export const DayViewName = styled.div`
  font-size: 14px;
  color: var(--grey-lighter);
  font-weight: 400;
  text-transform: capitalize;
`

export const MonthHeaderRow = styled.div<{ $dayCount: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $dayCount }) => $dayCount}, 1fr);
  border-bottom: 1px solid var(--border-light);
  flex-shrink: 0;

  div {
    padding: 10px 0;
    text-align: center;
    font-size: 11px;
    font-weight: 500;
    color: var(--grey-lighter);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-left: 1px solid var(--border-light);

    &:first-child {
      border-left: none;
    }
  }
`

export const MonthDays = styled.div<{ $dayCount: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $dayCount }) => $dayCount}, 1fr);
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`

export const MonthDay = styled.div<{ $otherMonth?: boolean; $dayCount: number }>`
  min-height: 110px;
  border-bottom: 1px solid var(--border-light);
  border-left: 1px solid var(--border-light);
  padding: 4px;
  transition: background 0.15s;
  background: ${({ $otherMonth }) => ($otherMonth ? 'var(--background)' : 'var(--white)')};

  &:nth-child(${({ $dayCount }) => $dayCount}n + 1) {
    border-left: none;
  }

  &:hover {
    background: var(--background);
  }
`

export const MonthDayHeader = styled.div`
  padding: 4px 4px 2px;
`

export const MonthEvent = styled.button<{ $color: string }>`
  display: block;
  width: calc(100% - 4px);
  text-align: left;
  padding: 2px 6px;
  margin: 1px 2px;
  border-radius: 4px;
  border: none;
  font-size: 11px;
  color: var(--white);
  background: ${({ $color }) => $color};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  font-weight: 400;
  line-height: 1.4;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.85;
  }
`

export const MoreEventsButton = styled.button`
  display: block;
  width: calc(100% - 4px);
  text-align: left;
  padding: 2px 6px;
  margin: 2px 2px 0;
  font-size: 14px;
  font-weight: 500;
  color: var(--primary);
  cursor: pointer;
  border-radius: 4px;
  border: none;
  background: transparent;
  transition: background 0.15s;

  &:hover {
    background: var(--primary-hover-bg);
  }
`

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

const slideUp = keyframes`
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`

export const DayEventsOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: var(--overlay);
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.15s ease;
`

export const DayEventsDialog = styled.div`
  background: var(--white);
  border-radius: 16px;
  width: 400px;
  max-width: calc(100% - 32px);
  max-height: 520px;
  overflow: hidden;
  box-shadow: 0 24px 48px var(--shadow-lg);
  animation: ${slideUp} 0.2s ease;
  display: flex;
  flex-direction: column;
`

export const DayEventsHeader = styled.div`
  padding: 20px 24px 12px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  border-bottom: 1px solid var(--border-light);
`

export const DayEventsDate = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
`

export const DayEventsDayName = styled.div`
  font-weight: 500;
  color: var(--text-primary);
  font-size: 18px;
  margin-bottom: 2px;
  text-transform: capitalize;
`

export const DayEventsClose = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 22px;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  transition: background 0.15s;

  &:hover {
    background: var(--primary-hover-bg);
  }
`

export const DayEventsCount = styled.div`
  padding: 4px 24px 8px;
  font-size: 12px;
  color: var(--grey-lighter);
`

export const DayEventsList = styled.div`
  padding: 8px 16px 20px;
  overflow-y: auto;
  max-height: 420px;
`

export const DayEventsItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 8px;
  border-radius: 8px;
  transition: background 0.15s;

  &:hover {
    background: var(--background);
  }
`

export const DayEventsItemColor = styled.div<{ $color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 4px;
  flex-shrink: 0;
  margin-top: 4px;
  background: ${({ $color }) => $color};
`

export const DayEventsItemInfo = styled.div`
  flex: 1;
  min-width: 0;
`

export const DayEventsItemTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 2px;
`

export const DayEventsItemDetails = styled.div`
  font-size: 12px;
  color: var(--grey-lighter);
`
